import { useCallback, useEffect, useMemo, useReducer, useRef, useState } from "react"
import { Trash2 } from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  useDeleteOfferVertical,
  type NetworkDomains,
  type PixelOption,
  type VerticalOption,
} from "@/api/hooks/offers"
import {
  VERTICAL_PRESETS,
  defaultVerticalDisplayName,
  slugifyVerticalName,
} from "@/lib/verticalCatalog"
import { buildTrackingClickUrl } from "@/lib/trackingClickUrl"
import type { EditingOffer } from "../state/offersReducer"

/**
 * Create/Edit offer modal. Parity: `offers/index.html:958-1047`.
 *
 * Fields vary by network:
 *   - ADW: title, keywords (min 3), RAC, vertical, pixel, event_name
 *   - BRM: headline, keywords, RAC, partner_domain
 *   - Default: target_url
 */

const OFFER_EVENT_NAMES = ["Lead", "CompletePayment"] as const

/** Legacy ADW rows may still have SubmitForm in the DB; map to Lead in the form. */
function normalizeOfferEventName(name: string | null | undefined): string {
  if (name === "CompletePayment") return "CompletePayment"
  return "Lead"
}

interface OfferFormState {
  name: string
  networkValue: string
  headline: string
  rac: string
  targetUrl: string
  verticalSlug: string
  pixelDbId: string
  eventName: string
  adwKeywords: string[]
  brmKeywords: string[]
  verticalOpen: boolean
  verticalSearch: string
}

type OfferFormAction =
  | { type: "SET_FIELD"; field: keyof OfferFormState; value: string | boolean | string[] }
  | { type: "SET_ADW_KEYWORDS"; keywords: string[] }
  | { type: "SET_BRM_KEYWORDS"; keywords: string[] }
  | { type: "RESET"; state: OfferFormState }

const INITIAL_STATE: OfferFormState = {
  name: "",
  networkValue: "",
  headline: "",
  rac: "",
  targetUrl: "",
  verticalSlug: "",
  pixelDbId: "",
  eventName: "Lead",
  adwKeywords: ["", "", ""],
  brmKeywords: [""],
  verticalOpen: false,
  verticalSearch: "",
}

function formReducer(state: OfferFormState, action: OfferFormAction): OfferFormState {
  switch (action.type) {
    case "SET_FIELD":
      return { ...state, [action.field]: action.value }
    case "SET_ADW_KEYWORDS":
      return { ...state, adwKeywords: action.keywords }
    case "SET_BRM_KEYWORDS":
      return { ...state, brmKeywords: action.keywords }
    case "RESET":
      return action.state
  }
}

interface OfferModalProps {
  open: boolean
  onClose: () => void
  editingOffer: EditingOffer | null
  pixels: PixelOption[]
  networkDomains: NetworkDomains
  preferredNetwork?: "ADW" | "BRM" | "Roots"
  verticalOptions?: VerticalOption[]
  /** When true, Roots vertical dropdown shows “Add vertical” (admins only). */
  isAdmin?: boolean
  isLoadingOptions?: boolean
  onSubmit: (data: Record<string, unknown>) => Promise<void>
  isSaving: boolean
}

export function OfferModal({
  open,
  onClose,
  editingOffer,
  pixels,
  networkDomains,
  preferredNetwork,
  verticalOptions = [],
  isAdmin = false,
  isLoadingOptions = false,
  onSubmit,
  isSaving,
}: OfferModalProps) {
  const isEdit = editingOffer?.id != null

  const editingOfferId = editingOffer?.id
  const editingPublicSlug = editingOffer?.public_slug
  const trackingDestinationUrl = useMemo(() => {
    if (editingOfferId == null || !editingPublicSlug) return null
    return buildTrackingClickUrl(editingPublicSlug)
  }, [editingOfferId, editingPublicSlug])

  // Form state
  const [form, dispatch] = useReducer(formReducer, INITIAL_STATE)
  const {
    name,
    networkValue,
    headline,
    rac,
    targetUrl,
    verticalSlug,
    pixelDbId,
    eventName,
    adwKeywords,
    brmKeywords,
    verticalOpen,
    verticalSearch,
  } = form
  const [rootsPixelError, setRootsPixelError] = useState<string>("")
  const [newVerticalDialogOpen, setNewVerticalDialogOpen] = useState(false)
  const [newVerticalName, setNewVerticalName] = useState("")
  /** Verticals added in-session until refetch; merged into the Roots vertical list. */
  const [localVerticalExtras, setLocalVerticalExtras] = useState<
    VerticalOption[]
  >([])

  const verticalRef = useRef<HTMLDivElement>(null)

  // Drop in-session vertical extras when the modal closes.
  const [prevOpen, setPrevOpen] = useState(open)
  if (open !== prevOpen) {
    setPrevOpen(open)
    if (!open) setLocalVerticalExtras([])
  }

  // Build network dropdown options from networkDomains
  const networkOptions = useMemo(() => {
    const opts: Array<{ value: string; label: string; network: string; domain: string }> = []
    // Always include ADW - MuhenDisiz (hardcoded parity)
    opts.push({
      value: "ADW - MuhenDisiz",
      label: "ADW - MuhenDisiz",
      network: "ADW",
      domain: "MuhenDisiz",
    })
    // Add BRM domains if available
    if (networkDomains.BRM) {
      for (const domain of networkDomains.BRM) {
        opts.push({
          value: `BRM - ${domain}`,
          label: `BRM - ${domain}`,
          network: "BRM",
          domain,
        })
      }
    }
    // Roots network
    opts.push({
      value: "Roots",
      label: "Roots",
      network: "Roots",
      domain: "",
    })
    return opts
  }, [networkDomains])

  // Parse network value
  const parsed = useMemo(() => {
    const parts = networkValue.split(" - ")
    return {
      network: parts[0] || "",
      domain: parts.slice(1).join(" - ") || "",
    }
  }, [networkValue])

  const isADW = parsed.network.toUpperCase() === "ADW"
  const isBRM = parsed.network.toUpperCase() === "BRM"
  const isRoots = parsed.network === "Roots"
  const isRootsLocked = preferredNetwork === "Roots"

  // Populate form when editing
  useEffect(() => {
    if (!open) return
    if (editingOffer) {
      const net = editingOffer.network || ""
      const domain = editingOffer.partner_domain || ""
      let nv: string
      if (net.toUpperCase() === "ADW") {
        nv = "ADW - MuhenDisiz"
      } else if (domain) {
        nv = `${net} - ${domain}`
      } else {
        nv = net
      }

      // Parse keywords from terms
      const terms = editingOffer.terms || ""
      let editAdwKeywords = ["", "", ""]
      let editBrmKeywords = [""]
      if (net.toUpperCase() === "ADW") {
        const kws = terms.split(",").map((k) => k.trim()).filter(Boolean)
        editAdwKeywords = kws.length > 0 ? kws : ["", "", ""]
      } else if (net.toUpperCase() === "BRM") {
        const kws = terms.split(", ").map((k) => k.trim()).filter(Boolean)
        editBrmKeywords = kws.length > 0 ? kws : [""]
      }

      dispatch({
        type: "RESET",
        state: {
          name: editingOffer.name || "",
          networkValue: nv,
          headline: editingOffer.headline || "",
          rac: editingOffer.rac || "",
          targetUrl: editingOffer.target_url || "",
          verticalSlug: (editingOffer.vertical_slug || "").trim(),
          pixelDbId: editingOffer.pixel_db_id ? String(editingOffer.pixel_db_id) : "",
          eventName: normalizeOfferEventName(editingOffer.event_name),
          adwKeywords: editAdwKeywords,
          brmKeywords: editBrmKeywords,
          verticalOpen: false,
          verticalSearch: "",
        },
      })
    } else {
      const preferredNetworkValue =
        preferredNetwork === "ADW"
          ? networkOptions.find((opt) => opt.network === "ADW")?.value
          : preferredNetwork === "BRM"
            ? networkOptions.find((opt) => opt.network === "BRM")?.value
            : preferredNetwork === "Roots"
              ? networkOptions.find((opt) => opt.network === "Roots")?.value
              : undefined

      // Reset for create
      dispatch({
        type: "RESET",
        state: {
          ...INITIAL_STATE,
          networkValue: preferredNetworkValue ?? networkOptions[0]?.value ?? "",
          pixelDbId: pixels.length === 1 ? String(pixels[0].id) : "",
        },
      })
    }
  }, [open, editingOffer, networkOptions, pixels, preferredNetwork])

  // Auto-select pixel if exactly 1
  useEffect(() => {
    if (pixels.length === 1 && !pixelDbId) {
      dispatch({ type: "SET_FIELD", field: "pixelDbId", value: String(pixels[0].id) })
    }
  }, [pixels, pixelDbId])

  // Outside-click closes vertical dropdown
  useEffect(() => {
    if (!verticalOpen) return
    const handler = (e: MouseEvent) => {
      if (verticalRef.current && !verticalRef.current.contains(e.target as Node)) {
        dispatch({ type: "SET_FIELD", field: "verticalOpen", value: false })
      }
    }
    document.addEventListener("mousedown", handler)
    return () => document.removeEventListener("mousedown", handler)
  }, [verticalOpen])

  // Keyword field management
  const handleAdwKeywordChange = useCallback(
    (idx: number, value: string) => {
      const next = [...adwKeywords]
      next[idx] = value
      if (idx === next.length - 1 && value.trim() && next.length < 10) {
        next.push("")
      }
      dispatch({ type: "SET_ADW_KEYWORDS", keywords: next })
    },
    [adwKeywords],
  )

  const handleBrmKeywordChange = useCallback(
    (idx: number, value: string) => {
      const next = [...brmKeywords]
      next[idx] = value
      if (idx === next.length - 1 && value.trim() && next.length < 10) {
        next.push("")
      }
      dispatch({ type: "SET_BRM_KEYWORDS", keywords: next })
    },
    [brmKeywords],
  )

  const normalizedVerticalOptions = useMemo(() => {
    const byKey = new Map<string, VerticalOption>()
    const add = (o: VerticalOption) => {
      const slug = o.slug.trim()
      const k = slug.toLowerCase()
      if (!k) return
      const name =
        (o.name || "").trim() || defaultVerticalDisplayName(slug)
      byKey.set(k, { slug, name })
    }
    for (const p of VERTICAL_PRESETS) add({ slug: p.slug, name: p.name })
    for (const o of verticalOptions) add({ ...o })
    for (const o of localVerticalExtras) add({ ...o })
    if (editingOffer) {
      const s = (editingOffer.vertical_slug || "").trim()
      if (s) {
        const n =
          (editingOffer.vertical_name || "").trim() ||
          defaultVerticalDisplayName(s)
        add({ slug: s, name: n })
      }
    }
    const cur = verticalSlug.trim()
    if (cur) {
      const key = cur.toLowerCase()
      if (!byKey.has(key)) {
        byKey.set(key, {
          slug: cur,
          name: defaultVerticalDisplayName(cur),
        })
      }
    }
    return Array.from(byKey.values()).sort((a, b) =>
      a.name.localeCompare(b.name),
    )
  }, [verticalOptions, localVerticalExtras, verticalSlug, editingOffer])

  const verticalDisplayName = useMemo(() => {
    if (!verticalSlug.trim()) return ""
    const key = verticalSlug.trim().toLowerCase()
    return (
      normalizedVerticalOptions.find(
        (o) => o.slug.trim().toLowerCase() === key,
      )?.name ?? defaultVerticalDisplayName(verticalSlug)
    )
  }, [normalizedVerticalOptions, verticalSlug])

  const filteredVerticalOptions = useMemo(() => {
    if (!verticalSearch.trim()) return normalizedVerticalOptions
    const q = verticalSearch.toLowerCase()
    return normalizedVerticalOptions.filter(
      (o) =>
        o.name.toLowerCase().includes(q) ||
        o.slug.toLowerCase().includes(q),
    )
  }, [normalizedVerticalOptions, verticalSearch])

  const handleCreateRootsVertical = useCallback(() => {
    setNewVerticalName(
      verticalSearch.trim() || verticalDisplayName || "",
    )
    setNewVerticalDialogOpen(true)
    dispatch({ type: "SET_FIELD", field: "verticalOpen", value: false })
  }, [verticalDisplayName, verticalSearch])

  const handleSaveNewVertical = useCallback(() => {
    const display = newVerticalName.trim()
    if (!display) return
    const slug = slugifyVerticalName(display)
    dispatch({ type: "SET_FIELD", field: "verticalSlug", value: slug })
    dispatch({ type: "SET_FIELD", field: "verticalSearch", value: "" })
    setLocalVerticalExtras((prev) => {
      const k = slug.toLowerCase()
      if (prev.some((p) => p.slug.trim().toLowerCase() === k)) return prev
      return [...prev, { slug, name: display }]
    })
    setNewVerticalDialogOpen(false)
    setNewVerticalName("")
  }, [newVerticalName])

  const handleCancelNewVertical = useCallback(() => {
    setNewVerticalDialogOpen(false)
    setNewVerticalName("")
  }, [])

  const handleDialogOpenChange = useCallback(
    (nextOpen: boolean) => {
      if (nextOpen) return
      if (newVerticalDialogOpen) {
        handleCancelNewVertical()
        return
      }
      onClose()
    },
    [newVerticalDialogOpen, handleCancelNewVertical, onClose],
  )

  const deleteVerticalMutation = useDeleteOfferVertical()

  const handleAdminDeleteVertical = useCallback(
    async (o: VerticalOption, e: React.MouseEvent) => {
      e.preventDefault()
      e.stopPropagation()
      if (
        !window.confirm(
          `Remove vertical “${o.name}” (${o.slug}) from all offers? Active offers will lose this vertical; this cannot be undone.`,
        )
      ) {
        return
      }
      try {
        const res = await deleteVerticalMutation.mutateAsync(o.slug)
        if (verticalSlug.toLowerCase() === o.slug.toLowerCase()) {
          dispatch({ type: "SET_FIELD", field: "verticalSlug", value: "" })
        }
        setLocalVerticalExtras((prev) =>
          prev.filter((x) => x.slug.toLowerCase() !== o.slug.toLowerCase()),
        )
        window.alert(`Cleared from ${res.cleared} offer(s).`)
      } catch {
        window.alert("Failed to delete vertical. You may need admin access.")
      }
    },
    [deleteVerticalMutation, verticalSlug],
  )

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()

      if (!name.trim()) {
        alert("Offer name is required.")
        return
      }

      if (isADW) {
        if (!rac.trim()) {
          alert("RAC is required for ADW offers.")
          return
        }
        const validKeywords = adwKeywords.filter((k) => k.trim())
        if (validKeywords.length < 3) {
          alert(
            "ADW requires a minimum of 3 keywords. Please enter at least 3 keywords.",
          )
          return
        }
        if (!pixelDbId) {
          alert("Please select a pixel.")
          return
        }
        if (!verticalSlug.trim()) {
          alert("Please select a vertical.")
          return
        }
      }

      if (isRoots) {
        if (!targetUrl.trim()) {
          alert("Target URL is required for Roots offers.")
          return
        }
        if (!verticalSlug.trim()) {
          alert("Please select a vertical for Roots offers.")
          return
        }
        if (!pixelDbId || !eventName) {
          setRootsPixelError("Pixel and Event are required for Roots offers.")
          return
        }
        setRootsPixelError("")
      }

      if (!isADW && !isBRM && !isRoots && !targetUrl.trim()) {
        alert("Target URL is required for non-domain offers (except ADW).")
        return
      }

      const body: Record<string, unknown> = {
        name: name.trim(),
        network: parsed.network,
        status: "Active",
      }

      if (isADW) {
        const slug = verticalSlug.trim()
        const opt = normalizedVerticalOptions.find((o) => o.slug === slug)
        const vName =
          (opt?.name || "").trim() ||
          defaultVerticalDisplayName(slug)
        body.target_url = null
        body.partner_domain = null
        body.headline = headline.trim() || null
        body.terms = adwKeywords
          .filter((k) => k.trim())
          .map((k) => k.trim())
          .join(",")
        body.rac = rac.trim()
        body.vertical_slug = slug || null
        body.vertical_name = vName || null
        body.pixel_db_id = pixelDbId ? Number(pixelDbId) : null
        body.event_name = eventName
      } else if (isBRM) {
        body.target_url = null
        body.partner_domain = parsed.domain
        body.headline = headline.trim() || null
        body.terms = brmKeywords
          .filter((k) => k.trim())
          .map((k) => k.trim())
          .join(", ")
        body.rac = rac.trim() || null
      } else if (isRoots) {
        const slug = verticalSlug.trim()
        const opt = normalizedVerticalOptions.find((o) => o.slug === slug)
        const vName =
          (opt?.name || "").trim() ||
          defaultVerticalDisplayName(slug)
        body.target_url = targetUrl.trim()
        body.vertical_slug = slug || null
        body.vertical_name = vName || null
        body.pixel_db_id = pixelDbId ? Number(pixelDbId) : null
        body.event_name = eventName
      } else {
        body.target_url = targetUrl.trim()
      }

      await onSubmit(body)
    },
    [
      name,
      parsed,
      isADW,
      isBRM,
      isRoots,
      headline,
      rac,
      targetUrl,
      verticalSlug,
      normalizedVerticalOptions,
      pixelDbId,
      eventName,
      adwKeywords,
      brmKeywords,
      onSubmit,
    ],
  )

  return (
    <>
      <Dialog open={open} onOpenChange={handleDialogOpenChange}>
        <DialogContent
          className="max-h-[90vh] max-w-[600px] overflow-y-auto border-[#4a4a4a] bg-[#2a2a2a] text-[#e0e0e0]"
          onEscapeKeyDown={(event) => {
            if (newVerticalDialogOpen) {
              event.preventDefault()
              handleCancelNewVertical()
            }
          }}
        >
        <div className="relative">
        <DialogHeader>
          <DialogTitle>
            {isEdit ? "Edit Offer" : "Create Offer"}
          </DialogTitle>
        </DialogHeader>

        {isLoadingOptions ? (
          <p className="py-8 text-center text-sm text-[#888]">
            Loading form options…
          </p>
        ) : (
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Name */}
          <div>
            <Label htmlFor="offerName">Name</Label>
            <Input
              id="offerName"
              value={name}
              onChange={(e) => dispatch({ type: "SET_FIELD", field: "name", value: e.target.value })}
              placeholder="Dog Food US"
              required
              className="bg-[#1e1e1e] text-[#e0e0e0]"
            />
          </div>

          {/* Network / Domain */}
          {!isRootsLocked && (
            <div>
              <Label htmlFor="offerNetwork">Domain</Label>
              <Select value={networkValue} onValueChange={(v) => dispatch({ type: "SET_FIELD", field: "networkValue", value: v })}>
                <SelectTrigger className="bg-[#1e1e1e] text-[#e0e0e0]">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#2a2a2a] text-[#e0e0e0]">
                  {networkOptions.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* ADW fields */}
          {isADW && (
            <>
              <div>
                <Label htmlFor="adwTitle">Title</Label>
                <Input
                  id="adwTitle"
                  value={headline}
                  onChange={(e) => dispatch({ type: "SET_FIELD", field: "headline", value: e.target.value })}
                  placeholder="Article Title"
                  className="bg-[#1e1e1e] text-[#e0e0e0]"
                />
              </div>

              <div>
                <Label>Keywords (min 3)</Label>
                {adwKeywords.map((kw, idx) => (
                  <Input
                    key={idx}
                    value={kw}
                    onChange={(e) => handleAdwKeywordChange(idx, e.target.value)}
                    placeholder={`Keyword ${idx + 1}`}
                    className="mb-1 bg-[#1e1e1e] text-[#e0e0e0]"
                  />
                ))}
              </div>

              <div>
                <Label htmlFor="adwRac">RAC</Label>
                <Input
                  id="adwRac"
                  value={rac}
                  onChange={(e) => dispatch({ type: "SET_FIELD", field: "rac", value: e.target.value })}
                  placeholder="Ad creative description"
                  required
                  className="bg-[#1e1e1e] text-[#e0e0e0]"
                />
              </div>

              {/* Internal vertical (shared catalog + org-specific options) */}
              <div ref={verticalRef} className="relative">
                <Label>Vertical</Label>
                <button
                  type="button"
                  onClick={() => dispatch({ type: "SET_FIELD", field: "verticalOpen", value: !verticalOpen })}
                  className="flex h-9 w-full items-center justify-between rounded border border-[#4a4a4a] bg-[#1e1e1e] px-3 text-sm text-[#e0e0e0]"
                >
                  <span>
                    {verticalDisplayName || "Select vertical..."}
                  </span>
                  <span className="text-[0.7rem]" aria-hidden>&#9660;</span>
                </button>
                {verticalOpen && (
                  <div className="absolute left-0 z-50 mt-1 flex w-full max-h-[230px] flex-col rounded border border-[#4a4a4a] bg-[#2a2a2a] shadow-lg">
                    <div className="shrink-0 p-2 pb-1">
                      <Input
                        type="text"
                        placeholder="Search vertical..."
                        value={verticalSearch}
                        onChange={(e) => dispatch({ type: "SET_FIELD", field: "verticalSearch", value: e.target.value })}
                        className="h-8 bg-[#1e1e1e] text-sm text-[#e0e0e0]"
                      />
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
                      {filteredVerticalOptions.length === 0 ? (
                        <div className="px-2 py-1 text-sm text-[#888]">No verticals found</div>
                      ) : (
                        filteredVerticalOptions.map((o) => {
                          const selected =
                            verticalSlug.trim().toLowerCase() ===
                            o.slug.trim().toLowerCase()
                          return (
                            <div
                              key={o.slug}
                              className={`mb-0.5 flex w-full items-stretch gap-0.5 rounded ${
                                selected ? "bg-[#667eea]" : "hover:bg-[#3a3a3a]"
                              }`}
                            >
                              <button
                                type="button"
                                className={`min-w-0 flex-1 rounded px-2 py-1 text-left text-sm ${
                                  selected ? "text-white" : "text-[#e0e0e0]"
                                }`}
                                onClick={() => {
                                  dispatch({
                                    type: "SET_FIELD",
                                    field: "verticalSlug",
                                    value: o.slug,
                                  })
                                  dispatch({ type: "SET_FIELD", field: "verticalOpen", value: false })
                                  dispatch({ type: "SET_FIELD", field: "verticalSearch", value: "" })
                                }}
                              >
                                {o.name}
                              </button>
                              {isAdmin && (
                                <button
                                  type="button"
                                  title="Remove vertical from all offers"
                                  disabled={deleteVerticalMutation.isPending}
                                  className={`shrink-0 rounded px-2 py-1 disabled:opacity-40 ${
                                    selected
                                      ? "text-white/85 hover:text-white"
                                      : "text-[#888] hover:text-red-400"
                                  }`}
                                  onClick={(e) => void handleAdminDeleteVertical(o, e)}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden />
                                </button>
                              )}
                            </div>
                          )
                        })
                      )}
                    </div>
                  </div>
                )}
              </div>

              <div>
                <Label htmlFor="offerPixel">Select Pixel</Label>
                <Select value={pixelDbId} onValueChange={(v) => dispatch({ type: "SET_FIELD", field: "pixelDbId", value: v })}>
                  <SelectTrigger className="bg-[#1e1e1e] text-[#e0e0e0]">
                    <SelectValue placeholder="Select a pixel..." />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2a2a2a] text-[#e0e0e0]">
                    {pixels.map((p) => (
                      <SelectItem key={p.id} value={String(p.id)}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="offerEventName">Event Name</Label>
                <Select value={eventName} onValueChange={(v) => dispatch({ type: "SET_FIELD", field: "eventName", value: v })}>
                  <SelectTrigger className="bg-[#1e1e1e] text-[#e0e0e0]">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-[#2a2a2a] text-[#e0e0e0]">
                    {OFFER_EVENT_NAMES.map((en) => (
                      <SelectItem key={en} value={en}>
                        {en}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </>
          )}

          {/* BRM fields */}
          {isBRM && (
            <>
              <div>
                <Label htmlFor="brmHeadline">Headline (q)</Label>
                <Input
                  id="brmHeadline"
                  value={headline}
                  onChange={(e) => dispatch({ type: "SET_FIELD", field: "headline", value: e.target.value })}
                  placeholder="Article Title"
                  className="bg-[#1e1e1e] text-[#e0e0e0]"
                />
              </div>

              <div>
                <Label>Keywords</Label>
                {brmKeywords.map((kw, idx) => (
                  <Input
                    key={idx}
                    value={kw}
                    onChange={(e) => handleBrmKeywordChange(idx, e.target.value)}
                    placeholder={`Keyword ${idx + 1}`}
                    className="mb-1 bg-[#1e1e1e] text-[#e0e0e0]"
                  />
                ))}
              </div>

              <div>
                <Label htmlFor="brmRac">RAC</Label>
                <Input
                  id="brmRac"
                  value={rac}
                  onChange={(e) => dispatch({ type: "SET_FIELD", field: "rac", value: e.target.value })}
                  placeholder="Ad creative description"
                  className="bg-[#1e1e1e] text-[#e0e0e0]"
                />
              </div>
            </>
          )}

          {/* Roots fields */}
          {isRoots && (
            <>
              <div>
                <Label htmlFor="rootsTargetUrl">Target URL</Label>
                <Input
                  id="rootsTargetUrl"
                  value={targetUrl}
                  onChange={(e) => dispatch({ type: "SET_FIELD", field: "targetUrl", value: e.target.value })}
                  placeholder="https://partner.com/offer"
                  required
                  className="bg-[#1e1e1e] text-[#e0e0e0]"
                />
              </div>

              <div ref={verticalRef} className="relative">
                <Label>Vertical</Label>
                <button
                  type="button"
                  onClick={() => dispatch({ type: "SET_FIELD", field: "verticalOpen", value: !verticalOpen })}
                  className="flex h-9 w-full items-center justify-between rounded border border-[#4a4a4a] bg-[#1e1e1e] px-3 text-sm text-[#e0e0e0]"
                >
                  <span>
                    {verticalDisplayName || "Select vertical..."}
                  </span>
                  <span className="text-[0.7rem]" aria-hidden>&#9660;</span>
                </button>
                {verticalOpen && (
                  <div className="absolute left-0 z-50 mt-1 flex w-full max-h-[230px] flex-col rounded border border-[#4a4a4a] bg-[#2a2a2a] shadow-lg">
                    <div className="shrink-0 p-2 pb-1">
                      <Input
                        type="text"
                        placeholder="Search vertical..."
                        value={verticalSearch}
                        onChange={(e) => dispatch({ type: "SET_FIELD", field: "verticalSearch", value: e.target.value })}
                        className="h-8 bg-[#1e1e1e] text-sm text-[#e0e0e0]"
                      />
                    </div>
                    <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-2">
                      {filteredVerticalOptions.length === 0 ? (
                        <div className="px-2 py-1 text-sm text-[#888]">No verticals found</div>
                      ) : (
                        filteredVerticalOptions.map((o) => {
                          const selected =
                            verticalSlug.trim().toLowerCase() ===
                            o.slug.trim().toLowerCase()
                          return (
                            <div
                              key={o.slug}
                              className={`mb-0.5 flex w-full items-stretch gap-0.5 rounded ${
                                selected ? "bg-[#667eea]" : "hover:bg-[#3a3a3a]"
                              }`}
                            >
                              <button
                                type="button"
                                className={`min-w-0 flex-1 rounded px-2 py-1 text-left text-sm ${
                                  selected ? "text-white" : "text-[#e0e0e0]"
                                }`}
                                onClick={() => {
                                  dispatch({
                                    type: "SET_FIELD",
                                    field: "verticalSlug",
                                    value: o.slug,
                                  })
                                  dispatch({ type: "SET_FIELD", field: "verticalOpen", value: false })
                                  dispatch({ type: "SET_FIELD", field: "verticalSearch", value: "" })
                                }}
                              >
                                {o.name}
                              </button>
                              {isAdmin && (
                                <button
                                  type="button"
                                  title="Remove vertical from all offers"
                                  disabled={deleteVerticalMutation.isPending}
                                  className={`shrink-0 rounded px-2 py-1 disabled:opacity-40 ${
                                    selected
                                      ? "text-white/85 hover:text-white"
                                      : "text-[#888] hover:text-red-400"
                                  }`}
                                  onClick={(e) => void handleAdminDeleteVertical(o, e)}
                                >
                                  <Trash2 className="h-4 w-4" aria-hidden />
                                </button>
                              )}
                            </div>
                          )
                        })
                      )}
                    </div>
                    {isAdmin && (
                      <div className="shrink-0 border-t border-[#4a4a4a] p-2">
                        <Button
                          type="button"
                          onClick={handleCreateRootsVertical}
                          className="h-8 w-full bg-[#4b5563] text-xs text-white hover:bg-[#5a6575]"
                        >
                          Add vertical
                        </Button>
                      </div>
                    )}
                  </div>
                )}
              </div>

              <div className="grid gap-3 md:grid-cols-2">
                <div>
                  <Label htmlFor="rootsPixel">Pixel</Label>
                  <Select
                    value={pixelDbId}
                    onValueChange={(v) => {
                      dispatch({ type: "SET_FIELD", field: "pixelDbId", value: v })
                      setRootsPixelError("")
                    }}
                  >
                    <SelectTrigger className="bg-[#1e1e1e] text-[#e0e0e0]">
                      <SelectValue placeholder="Select pixel..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2a2a2a] text-[#e0e0e0]">
                      {pixels.map((p) => (
                        <SelectItem key={p.id} value={String(p.id)}>
                          {p.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label htmlFor="rootsEventName">Event</Label>
                  <Select
                    value={eventName}
                    onValueChange={(v) => {
                      dispatch({ type: "SET_FIELD", field: "eventName", value: v })
                      setRootsPixelError("")
                    }}
                  >
                    <SelectTrigger className="bg-[#1e1e1e] text-[#e0e0e0]">
                      <SelectValue placeholder="Select event..." />
                    </SelectTrigger>
                    <SelectContent className="bg-[#2a2a2a] text-[#e0e0e0]">
                      {OFFER_EVENT_NAMES.map((en) => (
                        <SelectItem key={en} value={en}>
                          {en}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              {rootsPixelError && (
                <p className="mt-1 text-xs text-red-400">{rootsPixelError}</p>
              )}
            </>
          )}

          {/* Non-ADW non-BRM non-Roots: target URL */}
          {!isADW && !isBRM && !isRoots && (
            <div>
              <Label htmlFor="offerTargetUrl">Target URL</Label>
              <Input
                id="offerTargetUrl"
                value={targetUrl}
                onChange={(e) => dispatch({ type: "SET_FIELD", field: "targetUrl", value: e.target.value })}
                placeholder="https://partner.com/offer"
                required
                className="bg-[#1e1e1e] text-[#e0e0e0]"
              />
            </div>
          )}

          {trackingDestinationUrl && (
            <div className="rounded-md border border-[#4a4a4a] bg-[#1a1a1a] p-3">
              <Label className="text-[#e0e0e0]">
                TikTok destination URL
              </Label>
              <p className="mt-1 text-xs leading-snug text-[#a0a0a0]">
                Paste this as the ad landing page (not the Roots lander in Target
                URL). Fill{" "}
                <span className="font-mono text-[#c8c8c8]">atxt=</span> with your ad
                headline/copy before launch — it is forwarded to the Rsoc URL as{" "}
                <span className="font-mono text-[#c8c8c8]">ascr=</span>.
              </p>
              <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
                <Input
                  readOnly
                  value={trackingDestinationUrl}
                  className="font-mono text-xs bg-[#101010] text-[#e0e0e0]"
                />
                <Button
                  type="button"
                  variant="secondary"
                  className="shrink-0 bg-[#3a3a3a] text-[#e0e0e0] hover:bg-[#4a4a4a]"
                  onClick={() => {
                    void navigator.clipboard.writeText(trackingDestinationUrl)
                  }}
                >
                  Copy
                </Button>
              </div>
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <Button
              type="button"
              variant="secondary"
              onClick={onClose}
              className="bg-[#3a3a3a] text-[#e0e0e0] hover:bg-[#4a4a4a]"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={isSaving}
              className="bg-[#667eea] text-white hover:bg-[#5568d3]"
            >
              {isSaving
                ? "Saving..."
                : isEdit
                  ? "Save Changes"
                  : "Create Offer"}
            </Button>
          </div>
        </form>
        )}

        {newVerticalDialogOpen && (
          <div
            className="absolute inset-0 z-50 flex items-center justify-center bg-black/60 p-4"
            role="presentation"
            onMouseDown={(e) => {
              if (e.target === e.currentTarget) handleCancelNewVertical()
            }}
          >
            <div
              className="w-full max-w-[420px] rounded-lg border border-[#4a4a4a] bg-[#2a2a2a] p-4 shadow-xl"
              role="dialog"
              aria-modal="true"
              aria-labelledby="new-vertical-title"
              onMouseDown={(e) => e.stopPropagation()}
            >
              <h3
                id="new-vertical-title"
                className="text-base font-semibold text-[#e0e0e0]"
              >
                New vertical
              </h3>
              <p className="mt-1 text-sm text-[#a0a0a0]">
                Enter the vertical name as it should appear in the list (for
                example &quot;Depression Test&quot;). A URL-safe slug is stored
                on the server (for example depression_test).
              </p>
              <Input
                value={newVerticalName}
                onChange={(e) => setNewVerticalName(e.target.value)}
                placeholder="e.g. Depression Test"
                className="mt-3 bg-[#1e1e1e] text-[#e0e0e0]"
                autoFocus
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault()
                    handleSaveNewVertical()
                  }
                }}
              />
              <div className="mt-4 flex justify-end gap-2">
                <Button
                  type="button"
                  variant="secondary"
                  onClick={handleCancelNewVertical}
                  className="bg-[#3a3a3a] text-[#e0e0e0] hover:bg-[#4a4a4a]"
                >
                  Cancel
                </Button>
                <Button
                  type="button"
                  onClick={handleSaveNewVertical}
                  disabled={!newVerticalName.trim()}
                  className="bg-[#667eea] text-white hover:bg-[#5568d3]"
                >
                  Use vertical
                </Button>
              </div>
            </div>
          </div>
        )}
        </div>
      </DialogContent>
    </Dialog>
    </>
  )
}
